import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Search, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface Chat {
  id: string;
  name: string | null;
  is_group: boolean | null;
  otherParticipant?: {
    name: string;
    profile_image: string | null;
  };
}

interface ForwardMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageText: string;
  attachmentUrl?: string;
  senderName?: string;
}

export const ForwardMessageDialog = ({
  open,
  onOpenChange,
  messageText,
  attachmentUrl,
  senderName,
}: ForwardMessageDialogProps) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [forwarding, setForwarding] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [forwardedChats, setForwardedChats] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open && user) {
      fetchChats();
      setForwardedChats(new Set());
      setSearchQuery("");
    }
  }, [open, user]);

  const fetchChats = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: participantData } = await supabase
        .from("chat_participants")
        .select("chat_id")
        .eq("user_id", user.id);

      if (!participantData) return;

      const chatIds = participantData.map((p) => p.chat_id);
      const { data: chatsData } = await supabase
        .from("chats")
        .select("*")
        .in("id", chatIds);

      if (!chatsData) return;

      const enrichedChats: Chat[] = [];
      for (const chat of chatsData) {
        if (chat.is_group) {
          enrichedChats.push(chat);
        } else {
          const { data: participants } = await supabase
            .from("chat_participants")
            .select("user_id")
            .eq("chat_id", chat.id)
            .neq("user_id", user.id)
            .limit(1);

          let otherParticipant = undefined;
          if (participants && participants[0]) {
            const { data: profileData } = await supabase.rpc(
              "get_chat_participant_profile",
              { _profile_id: participants[0].user_id }
            );
            if (profileData && profileData[0]) {
              otherParticipant = {
                name: profileData[0].name,
                profile_image: profileData[0].profile_image,
              };
            }
          }
          enrichedChats.push({ ...chat, otherParticipant });
        }
      }
      setChats(enrichedChats);
    } catch (error) {
      console.error("Error fetching chats for forward:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleForward = async (chatId: string) => {
    if (!user || forwarding) return;
    setForwarding(chatId);

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("preferred_language")
        .eq("id", user.id)
        .single();

      const forwardPrefix = `↪ ${senderName || ""}:\n`;
      const forwardedText = forwardPrefix + messageText;

      const messageData: any = {
        chat_id: chatId,
        sender_id: user.id,
        original_text: forwardedText,
        sender_language: profile?.preferred_language || "en",
        translated_texts: {},
      };

      if (attachmentUrl) {
        messageData.attachment_url = attachmentUrl;
      }

      const { error } = await supabase.from("messages").insert(messageData);

      if (error) throw error;

      setForwardedChats((prev) => new Set(prev).add(chatId));
      toast({
        title: t("success"),
        description: t("messageForwarded"),
      });
    } catch (error) {
      console.error("Error forwarding message:", error);
      toast({
        title: t("error"),
        description: t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setForwarding(null);
    }
  };

  const getChatDisplayName = (chat: Chat) => {
    if (chat.is_group) return chat.name || "Group";
    return chat.otherParticipant?.name || "Chat";
  };

  const getChatImage = (chat: Chat) => {
    if (!chat.is_group) return chat.otherParticipant?.profile_image;
    return undefined;
  };

  const filteredChats = chats.filter((chat) => {
    const name = getChatDisplayName(chat).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("forwardMessage")}</DialogTitle>
        </DialogHeader>
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="bg-muted/50 rounded-lg px-3 py-2 mb-2 border-l-2 border-primary">
          <p className="text-xs text-muted-foreground truncate">{messageText}</p>
        </div>
        <ScrollArea className="max-h-[300px]">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredChats.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">
              {t("noResults")}
            </p>
          ) : (
            <div className="space-y-1">
              {filteredChats.map((chat) => {
                const isForwarded = forwardedChats.has(chat.id);
                return (
                  <button
                    key={chat.id}
                    onClick={() => !isForwarded && handleForward(chat.id)}
                    disabled={forwarding === chat.id || isForwarded}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors disabled:opacity-60"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={getChatImage(chat) || undefined} />
                      <AvatarFallback>
                        {getChatDisplayName(chat)[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1 text-left text-sm font-medium truncate">
                      {getChatDisplayName(chat)}
                    </span>
                    {forwarding === chat.id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : isForwarded ? (
                      <Check className="h-4 w-4 text-primary" />
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs">
                        {t("send")}
                      </Button>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
